// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { apiService } from "../../../services/api/apiService";
import { toBootstrapTenantError } from "./bootstrapTenantError";
import type {
  BootstrapTenantInputModel,
  BootstrapTenantModel,
  InvitationInputModel,
  InvitationModel,
  JoinTenantInputModel,
  JoinTenantModel,
  OnboardingSessionModel,
} from "../models/onboardingModels";
import type {
  ApiEnvelopeDto,
  BootstrapTenantRequestDto,
  BootstrapTenantResponseDto,
  InvitationRequestDto,
  InvitationResponseDto,
  JoinTenantRequestDto,
  JoinTenantResponseDto,
  OnboardingSessionDto,
} from "../dto/onboardingDtos";
import {
  mapBootstrapTenantResponseDtoToModel,
  mapInvitationResponseDtoToModel,
  mapJoinTenantResponseDtoToModel,
  mapOnboardingSessionDtoToModel,
  toBootstrapTenantRequestDto,
  toInvitationRequestDto,
  toJoinTenantRequestDto,
} from "../mappers/onboardingMappers";

function unwrapApiEnvelope<T>(envelope: ApiEnvelopeDto<T>): T {
  if (envelope.error !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

export async function getOnboardingSession(
  token: string,
): Promise<OnboardingSessionModel> {
  const response = await apiService.requestJson<
    ApiEnvelopeDto<OnboardingSessionDto>
  >({
    path: "/api/onboarding/session",
    method: "GET",
    token,
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapOnboardingSessionDtoToModel)
    .unwrap();
}

export async function bootstrapTenant(
  token: string,
  request: BootstrapTenantInputModel,
): Promise<BootstrapTenantModel> {
  let response;
  try {
    response = await apiService.requestJson<
      ApiEnvelopeDto<BootstrapTenantResponseDto>,
      BootstrapTenantRequestDto
    >({
      path: "/api/onboarding/bootstrap",
      method: "POST",
      token,
      body: toBootstrapTenantRequestDto(request),
    });
  } catch (error: unknown) {
    throw toBootstrapTenantError(error);
  }

  return response
    .map(unwrapApiEnvelope)
    .map(mapBootstrapTenantResponseDtoToModel)
    .unwrap();
}

export async function issueInvitation(
  token: string,
  request: InvitationInputModel,
): Promise<InvitationModel> {
  const response = await apiService.requestJson<
    ApiEnvelopeDto<InvitationResponseDto>,
    InvitationRequestDto
  >({
    path: "/api/onboarding/invitations",
    method: "POST",
    token,
    body: toInvitationRequestDto(request),
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapInvitationResponseDtoToModel)
    .unwrap();
}

export async function joinTenant(
  token: string,
  request: JoinTenantInputModel,
): Promise<JoinTenantModel> {
  const response = await apiService.requestJson<
    ApiEnvelopeDto<JoinTenantResponseDto>,
    JoinTenantRequestDto
  >({
    path: "/api/onboarding/join",
    method: "POST",
    token,
    body: toJoinTenantRequestDto(request),
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapJoinTenantResponseDtoToModel)
    .unwrap();
}
